import { Icon, Loading, SetupIntent, Logo } from '@arcadiapower/shrike';
import { useContext, useState } from 'react';
import { Button } from '~/components/button';
import { ACB_BILLING, DialogSelector, DUAL_BILLING } from '~/components/dialogs';
import { PaymentMethods } from '~/components/dropdowns';
import { ErrorMessage } from '~/components/error-message';
import { Form } from '~/components/form';
import {
  type ImageType,
  ImageSelector,
  HALFMOON_SOLAR,
  PAYMENT_IMAGE,
  ORLEANS_SOLAR,
} from '~/components/image';
import { TwoColumnGrid, Container } from '~/components/layout';
import { Link } from '~/components/link';
import { PaymentForm } from '~/components/payment-form';
import { Separator } from '~/components/separator';
import { TextCallout } from '~/components/text-callout';
import { Title, Description, Text } from '~/components/typography';
import { useCopy, useForm } from '~/hooks';
import { LocalizeContext } from '~/providers/localize';
import { BillingDataSource, Utility, User } from '~/typings/context';
import { IS_TEST } from '~/utils/constants';
import { getSavingsRateDescription } from '~/utils/funnels';
import { paymentMethodFromSetupIntent, SetupIntentDetails } from '~/utils/stripe';
import { TRACK } from '~/utils/tracking';
import { ExpanderItem } from './billing.style';

export const Billing = ({
  context,
  error,
  flags,
  loading,
  next,
  overrideCopy,
  pending,
  skip,
}: BillingProps) => {
  const copy = useCopy(context, defaultCopy, overrideCopy);
  const paymentMethods = context.existingPaymentMethods;
  const [showExisting, setShowExisting] = useState(!!paymentMethods.length);
  const [form, updateForm] = useForm({ existingPaymentMethodId: paymentMethods[0]?.id });
  const language = useContext(LocalizeContext);

  const submitExisting = () => next(form as BillingForm);

  const submitNew = (setupIntent: SetupIntent) => {
    if (setupIntent.status === 'succeeded') {
      next(paymentMethodFromSetupIntent(setupIntent));
    }
    // If the setupIntent status is `requires_action` and the next action type is `verify_with_microdeposits`,
    // then we can show proceed to the next screen. This is for the business funnel, which allows users
    // to manually enter their account and routing numbers and verify with microdeposits.
    if (
      setupIntent.status === 'requires_action' &&
      setupIntent.next_action?.type === 'verify_with_microdeposits'
    ) {
      next(paymentMethodFromSetupIntent(setupIntent));
    }
  };

  const [isOpen, setIsOpen] = useState(false);
  const toggleIsOpen = () => setIsOpen(isOpen => !isOpen);

  const [statementIsOpen, setStatementIsOpen] = useState(false);
  const toggleStatementIsOpen = () => setStatementIsOpen(statementIsOpen => !statementIsOpen);

  if (loading) return <Loading />;

  return (
    <TwoColumnGrid testId="screen-Billing">
      <TwoColumnGrid.Primary>
        <Title>{copy.title}</Title>
        <Description mb={5}>{copy.description}</Description>
        {context.experience === 'dual' && (
          <>
            <ExpanderItem data-testid="exapander-one">
              <Container
                role="button"
                aria-pressed={isOpen}
                tabIndex={0}
                onClick={toggleStatementIsOpen}
                onKeyDown={toggleStatementIsOpen}
                style={{ cursor: 'pointer' }}
                display="flex"
                alignItems="center"
                mb={2}
              >
                <Logo type="node" scale={2} margin={{ right: '16px', left: '16px', top: '5px' }} />
                <Text
                  textStyle="heading400"
                  paddingBottom="10px"
                  paddingTop="16px"
                  data-testid="exapander-one-text"
                >
                  Your Arcadia Statement
                </Text>
                <Icon
                  icon={statementIsOpen ? 'ChevronUp' : 'ChevronDown'}
                  style={{ marginRight: '8px' }}
                />
              </Container>
              {statementIsOpen && (
                <Text textStyle="paragraph400" opacity="low" padding="16px">
                  {copy.payArcadiaText}
                </Text>
              )}
            </ExpanderItem>
            <ExpanderItem data-testid="exapander-two">
              <Container
                role="button"
                aria-pressed={isOpen}
                tabIndex={0}
                onClick={toggleIsOpen}
                onKeyDown={toggleIsOpen}
                style={{ cursor: 'pointer' }}
                display="flex"
                alignItems="center"
                pr={2}
                mb={2}
              >
                <Logo type="node" scale={2} margin={{ right: '16px', left: '16px', top: '5px' }} />
                <Text
                  textStyle="heading400"
                  paddingBottom="10px"
                  paddingTop="16px"
                  data-testid="exapander-two-text"
                >{`Your ${context.utility.displayName} Bill`}</Text>
                <Icon icon={isOpen ? 'ChevronUp' : 'ChevronDown'} />
              </Container>
              {isOpen && (
                <Text textStyle="paragraph400" opacity="low" padding="16px">
                  {copy.payUtilityText}
                </Text>
              )}
            </ExpanderItem>
          </>
        )}
        <Description mb={6}>
          {copy.learnMoreModal && (
            <Icon icon="QuestionCircleInverted" margin={{ bottom: '2px', right: '8px' }} />
          )}
          <DialogSelector
            type={copy.learnMoreModal}
            target={
              <Link color="primary" track={TRACK.learnMoreCsClicked}>
                {copy.learnMoreText}
              </Link>
            }
          />
        </Description>
        {showExisting ? (
          <Form onSubmit={submitExisting}>
            <PaymentMethods
              onChange={updateForm}
              options={paymentMethods}
              value={form.existingPaymentMethodId}
            />
            <Button
              mode="primary"
              testId="confirm-existing-payment"
              loading={pending}
              mt={4}
              type="submit"
            >
              Confirm payment method
            </Button>
          </Form>
        ) : (
          <PaymentForm
            onSubmit={submitNew}
            pending={pending}
            stripeToken={context.stripeToken}
            locale={language}
          >
            {flags.showPaymentHoldNotice && <Text mt={4}>{copy.paymentHoldNotice}</Text>}
            {context.experience === 'acb' && (
              <TextCallout
                body={copy.acbCalloutBody}
                header={copy.acbCalloutHeader}
                icon="LightningDollarIcon"
                mt={6}
              />
            )}
          </PaymentForm>
        )}
        {paymentMethods.length > 0 && (
          <>
            <Separator innerText="or" />
            <Button
              mode="default"
              onClick={() => setShowExisting(!showExisting)}
              testId="toggle-payment"
            >
              {showExisting ? 'Add a new payment method' : 'Choose an existing payment method'}
            </Button>
          </>
        )}
        <ErrorMessage error={error} mt={2} />
        {skip && (
          <Button mode="default" testId="skip-billing-button" mt={4} onClick={skip}>
            Skip for now
          </Button>
        )}
      </TwoColumnGrid.Primary>
      <TwoColumnGrid.Secondary>
        <ImageSelector type={copy.imageType} />
        {IS_TEST && <TestSubmit next={next} showExisting={showExisting} />}
      </TwoColumnGrid.Secondary>
    </TwoColumnGrid>
  );
};

// this is solely to aid integration tests and will not be present in production
type TestSubmitProps = {
  next: BillingProps['next'];
  showExisting: boolean;
};
const TestSubmit = ({ next, showExisting }: TestSubmitProps) => {
  const handleSubmit = () => {
    if (showExisting) {
      next({ existingPaymentMethodId: 2 });
    } else {
      next({ paymentMethod: { lastFour: '1234', source: 'Visa', type: 'credit_card' } });
    }
  };
  return (
    <button data-testid="billing-test-submit" onClick={handleSubmit}>
      Test submit
    </button>
  );
};

export const defaultCopy = (context: BillingContext) => {
  const utilityName = context.utility.displayName;
  const savingsRate = getSavingsRateDescription(
    context.utility.state.abbreviation,
    context.funnelId,
    context.utility.id,
  );
  const isDual = context.experience === 'dual';
  const experience = {
    acb: 'From now on, you’ll manage and pay your monthly power bill automatically through our secure platform. Once you’re connected to a solar farm, you’ll pay for your solar credits the same way. Paying for solar credits means overall savings on your total power cost.',
    dual: (
      <>
        Once you are connected to a solar farm, you'll receive two bills. You're <b>guaranteed</b>{' '}
        lower monthly energy costs than what you would have without community solar.
      </>
    ),
  };

  const experienceImage = {
    acb: PAYMENT_IMAGE,
    dual: HALFMOON_SOLAR,
  };

  return {
    title: isDual
      ? 'Add a payment method for your Arcadia bill.'
      : 'Almost done! Now, choose a way to pay for your power bill and solar credits.',
    description: experience[context.experience || 'acb'] || experience.acb,
    imageType: ((context.experience && experienceImage[context.experience || 'acb']) ||
      ORLEANS_SOLAR) as ImageType,
    learnMoreModal: isDual ? DUAL_BILLING : ACB_BILLING,
    learnMoreText: isDual ? 'How do I save with two bills?' : 'Why pay through our platform?',
    payArcadiaText: `We'll send you a bill for your contribution to the solar farm — an automatic payment that helps keep your solar farm running. You'll see your solar savings of ${savingsRate} here.`,
    payUtilityText:
      "You'll receive and pay this like normal, but it will be lower because it won't include your contribution to the solar farm.",
    acbCalloutHeader: 'Get ready for your first bill',
    acbCalloutBody: `Please be sure to pay any remaining balance on your ${utilityName} account and cancel automatic bill payments through ${utilityName} if you had them turned on. Don't worry. We'll send you a reminder to your email after you complete signing up.`,
    paymentHoldNotice:
      "If you add a credit card, we'll place a temporary hold of $1 on your card. Don't worry - this is not a charge and will be released within 24 hours. Depending on your bank, it could take up to 7 days for your funds to be available.",
  };
};

export type BillingCopy = ReturnType<typeof defaultCopy>;

export interface BillingProps {
  context: BillingContext;
  error?: string;
  flags: {
    showPaymentHoldNotice: boolean;
  };
  loading: boolean;
  next: (payload: BillingForm) => void;
  overrideCopy?: (context: BillingContext) => Partial<BillingCopy>;
  pending: boolean;
  skip?: () => void;
}

export type BillingForm = { existingPaymentMethodId: number } | SetupIntentDetails;

export interface BillingContext {
  existingPaymentMethods: {
    id: number;
    lastFour: string;
    source: string;
  }[];
  stripeToken: string;
  experience?: BillingDataSource;
  utility: Utility;
  user: User;
  funnelId?: string;
  isUcbAvailableAsOption?: boolean;
}
